import type React from 'react';
import {Children, useLayoutEffect, useRef, useState} from 'react';
import {Box, Button} from '@mui/material';
import {KPI_GAP, pillsThatFit} from '../../domain/kpiRow';

const BUTTON_FALLBACK = 96;

/**
 * KPI pills on one line. When they do not all fit (narrow screens), the ones that do are shown with "Show more" right
 * after the last of them (on the next line when even one pill fills a phone screen); expanded, all pills wrap onto
 * further lines followed by "Show less".
 */
export const KpiRow: React.FC<{children: React.ReactNode}> = ({children}) => {
    const items = Children.toArray(children).filter(Boolean);
    const count = items.length;
    const container = useRef<HTMLDivElement>(null);
    const pills = useRef<(HTMLDivElement | null)[]>([]);
    const button = useRef<HTMLButtonElement>(null);
    const widths = useRef<number[]>([]);
    const [expanded, setExpanded] = useState(false);
    const [visible, setVisible] = useState(items.length);

    useLayoutEffect(() => {
        const el = container.current;
        if (!el) return undefined;
        const measure = () => {
            // Pill widths do not depend on the container, so keep the last width seen for pills that are hidden now.
            const known: number[] = [];
            for (let i = 0; i < count; i++) {
                const w = pills.current[i]?.offsetWidth;
                if (w) widths.current[i] = w;
                known.push(widths.current[i] ?? 0);
            }
            setVisible(pillsThatFit(known, el.clientWidth, button.current?.offsetWidth || BUTTON_FALLBACK));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [children, count]);

    const hidden = !expanded && visible < items.length;
    return (
        // Always wraps: collapsed, only the pills that fit are shown, so the row wraps only on a phone where even the
        // first pill leaves no room for "Show more", which then moves to the next line instead of out of view.
        <Box ref={container} sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: `${KPI_GAP}px`,
            minWidth: 0, overflow: 'hidden'}}>
            {items.map((child, i) => (
                <Box key={i} ref={(n: HTMLDivElement | null) => { pills.current[i] = n; }}
                     sx={{display: hidden && i >= visible ? 'none' : 'flex', flexShrink: 0}}>
                    {child}
                </Box>
            ))}
            {(hidden || (expanded && visible < items.length)) && (
                <Button ref={button} size="small" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}
                        sx={{flexShrink: 0, whiteSpace: 'nowrap'}}>
                    {expanded ? 'Show less' : `Show more (${items.length - visible})`}
                </Button>
            )}
        </Box>
    );
};
